import apiClient from "./axiosConfig";

/**
 * Price list (pricing catalog) APIs — BASE_URL includes /api prefix.
 *
 * GET    /products/price-lists/
 * GET    /products/price-lists/:id/
 * GET    /products/price-lists/full/
 * POST   /products/price-lists/              { name, index }
 * PATCH  /products/price-lists/:id/          { add_product_ids } | { remove_product_ids } | { index } | { name, index }
 * DELETE /products/price-lists/:id/
 */

const PRICE_LISTS = "/products/price-lists";

const extractList = (payload) => {
  const root = payload?.data ?? payload;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.results)) return root.results;
  if (Array.isArray(root?.price_lists)) return root.price_lists;
  if (Array.isArray(root?.groups)) return root.groups;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const unwrap = (data) => data?.data ?? data;

const getIndexing = (item) => {
  const raw = item.index ?? item.indexing ?? item.order ?? item.position ?? item.sort_order ?? 0;
  const num = Number(raw);
  return Number.isFinite(num) ? num : 0;
};

const sortByIndexing = (list) =>
  [...list].sort((a, b) => getIndexing(a) - getIndexing(b));

/** GET all price list groups (metadata / order). */
export const getPriceLists = async () => {
  const response = await apiClient.get(`${PRICE_LISTS}/`);
  return sortByIndexing(extractList(response.data));
};

/** GET single price list group with products. */
export const getPriceListDetail = async (groupId) => {
  const response = await apiClient.get(`${PRICE_LISTS}/${groupId}/`);
  return unwrap(response.data);
};

/** GET all groups with nested products (preferred for initial load). */
export const getPriceListsFull = async () => {
  const response = await apiClient.get(`${PRICE_LISTS}/full/`);
  return sortByIndexing(extractList(response.data));
};

/** POST create price list group. */
export const createPriceList = async ({ name, index }) => {
  const response = await apiClient.post(`${PRICE_LISTS}/`, {
    name,
    index: index != null ? String(index) : "1",
  });
  return unwrap(response.data);
};

/** PATCH price list — add products, update index, and/or name. Returns full group. */
export const patchPriceList = async (groupId, body = {}) => {
  const payload = { ...body };
  if (payload.index != null) payload.index = String(payload.index);
  const response = await apiClient.patch(`${PRICE_LISTS}/${groupId}/`, payload);
  return unwrap(response.data);
};

/** PATCH add inventory products to a price list group. */
export const addProductsToPriceList = async (groupId, productIds = []) => {
  const ids = productIds.filter((id) => id != null);
  if (!ids.length) {
    throw new Error("At least one product id is required");
  }
  return patchPriceList(groupId, { add_product_ids: ids });
};

/** PATCH remove products from a price list group. */
export const removeProductsFromPriceList = async (groupId, productIds = []) => {
  const ids = productIds.filter((id) => id != null);
  if (!ids.length) {
    throw new Error("At least one product id is required");
  }
  return patchPriceList(groupId, { remove_product_ids: ids });
};

/** PATCH group name and/or index. */
export const updatePriceList = async (groupId, { name, index } = {}) => {
  const payload = {};
  if (name != null) payload.name = name;
  if (index != null) payload.index = index;
  return patchPriceList(groupId, payload);
};

/** DELETE price list group (soft delete). */
export const deletePriceList = async (groupId) => {
  const response = await apiClient.delete(`${PRICE_LISTS}/${groupId}/`);
  return unwrap(response.data);
};

/** After drag: PATCH each group whose index changed (1-based string). */
export const updatePricingCatalogGroupOrder = async (
  groups = [],
  previousGroups = []
) => {
  const prevIndexById = new Map(
    previousGroups.map((group, i) => [
      group.id,
      String(group.index ?? group.indexing ?? i + 1),
    ])
  );

  const changed = groups
    .map((group, i) => ({
      id: group.id,
      index: String(i + 1),
    }))
    .filter(({ id, index }) => prevIndexById.get(id) !== index);

  if (!changed.length) return [];

  return Promise.all(
    changed.map(({ id, index }) => patchPriceList(id, { index }))
  );
};

/**
 * Load catalog for UI: uses /full/ (one request).
 * Fallback: /price-lists/ then GET each group by id.
 */
export const fetchPricingCatalogGroups = async () => {
  try {
    const full = await getPriceListsFull();
    if (full.length) return full;
  } catch (fullError) {
    console.warn("Price lists /full/ unavailable, falling back to per-group fetch:", fullError);
  }

  const orderList = await getPriceLists();
  if (!orderList.length) return [];

  const details = await Promise.all(
    orderList.map((item) => {
      const id = item.id ?? item.group_id ?? item.price_list_id;
      return getPriceListDetail(id);
    })
  );
  return details;
};

export const getPricingCatalogGroupOrder = getPriceLists;
export const getPricingCatalogGroupDetail = getPriceListDetail;
